<div class="card border-0 shadow-sm clinic-profile-card">
  <!-- Header -->
  <div class="card-header bg-white">
    <h5 class="mb-1 fw-semibold">
      <i class="bi bi-hospital text-primary me-2"></i>
      Practice Profile
    </h5>
    <small class="text-muted">
      Maintain the practice information patients and staff rely on for scheduling and communication.
    </small>
  </div>

  <div class="card-body">
    @if (!canEdit) {
      <div class="alert alert-info mb-4" role="status">
        Practice profile details are read-only for the current role.
      </div>
    }

    @if (isLoading) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
        Loading practice profile...
      </div>
    }

    @if (!isLoading) {
      <div class="alert alert-info d-flex align-items-start clinic-note">
        <i class="bi bi-info-circle-fill me-2 mt-1"></i>
        <div>
          These details support patient appointments, practice discovery, and day-to-day scheduling
          availability.
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="card clinic-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Practice Name</div>
              <div class="fs-5 fw-bold text-truncate">{{ clinicProfile.name || 'Not set' }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card clinic-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Location</div>
              <div class="fs-5 fw-bold text-truncate">{{ clinicProfile.city || 'Not set' }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card clinic-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Daily Hours</div>
              <div class="fs-5 fw-bold">
                {{ clinicProfile.openingTime }} - {{ clinicProfile.closingTime }}
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Practice Details -->
    <div class="row g-3 clinic-details-section">
      <div class="col-12">
        <label class="form-label fw-semibold">Practice Name</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="clinicProfile.name"
          [disabled]="!canEdit || isLoading || isSaving"
          placeholder="Enter practice name"
        />
      </div>
      <div class="col-12">
        <label class="form-label fw-semibold">Address</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="clinicProfile.address"
          [disabled]="!canEdit || isLoading || isSaving"
          placeholder="Street address"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label fw-semibold">City</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="clinicProfile.city"
          [disabled]="!canEdit || isLoading || isSaving"
        />
      </div>
      <div class="col-md-3">
        <label class="form-label fw-semibold">Province</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="clinicProfile.province"
          [disabled]="!canEdit || isLoading || isSaving"
        />
      </div>
      <div class="col-md-3">
        <label class="form-label fw-semibold">Postal Code</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="clinicProfile.postalCode"
          [disabled]="!canEdit || isLoading || isSaving"
        />
      </div>
    </div>

    <hr class="my-4" />

    <!-- Operating Hours -->
    <h6 class="fw-semibold mb-3">Operating Hours</h6>
    <div class="row g-3 clinic-hours-section">
      <div class="col-md-6">
        <label class="form-label">Opening Time</label>
        <input
          type="time"
          class="form-control"
          [(ngModel)]="clinicProfile.openingTime"
          [disabled]="!canEdit || isLoading || isSaving"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label">Closing Time</label>
        <input
          type="time"
          class="form-control"
          [(ngModel)]="clinicProfile.closingTime"
          [disabled]="!canEdit || isLoading || isSaving"
        />
      </div>
    </div>

    <div class="mt-3">
      <small class="text-muted">
        These hours are used for appointment scheduling and availability validation.
      </small>
    </div>

    <!-- Practice Invitations — only visible to doctors -->
    @if (isDoctor) {
      <hr class="my-4" />
      <div class="clinic-invitations-section">
        <h6 class="fw-semibold mb-1">
          <i class="bi bi-envelope-paper text-warning me-2"></i>
          Practice Invitations
        </h6>
        <small class="text-muted d-block mb-3">
          Practices that have invited you to join. Accept to become part of their team.
        </small>

        @if (isLoadingInvitations) {
          <div class="text-center py-3 text-muted">
            <div
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></div>
            Checking for invitations...
          </div>
        }

        @if (!isLoadingInvitations && pendingInvitations.length === 0) {
          <div class="alert alert-secondary py-2 d-flex align-items-center gap-2">
            <i class="bi bi-inbox"></i>
            <span>No pending practice invitations.</span>
          </div>
        }

        @if (!isLoadingInvitations && pendingInvitations.length > 0) {
          <div class="list-group list-group-flush">
            @for (invite of pendingInvitations; track invite.membershipId) {
              <div class="list-group-item border rounded mb-2 p-3">
                <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div class="fw-semibold">{{ invite.clinicName }}</div>
                    <div class="text-muted small">
                      <i class="bi bi-geo-alt me-1"></i>{{ invite.addressLine1 }}
                    </div>
                    <div class="text-muted small">
                      <i class="bi bi-person me-1"></i>
                      Invited by {{ invite.invitedByFirstName }} {{ invite.invitedByLastName }}
                    </div>
                    <div class="text-muted small">
                      <i class="bi bi-calendar me-1"></i
                      >{{ invite.invitedOn | date: 'dd MMM yyyy' }}
                    </div>
                  </div>
                  <div class="d-flex gap-2 mt-1">
                    <button
                      class="btn btn-success btn-sm"
                      [disabled]="respondingToId === invite.membershipId"
                      (click)="respondToInvitation(invite.membershipId, true)"
                    >
                      @if (respondingToId === invite.membershipId) {
                        <span
                          class="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <i class="bi bi-check-lg me-1"></i>
                      }
                      Accept
                    </button>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      [disabled]="respondingToId === invite.membershipId"
                      (click)="respondToInvitation(invite.membershipId, false)"
                    >
                      <i class="bi bi-x-lg me-1"></i>Decline
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>

  <!-- Footer -->
  <div
    class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-2"
  >
    <small class="text-muted">
      @if (canEdit) {
        Saved changes update the practice profile immediately.
      } @else {
        Contact a practice manager to request profile changes.
      }
    </small>
    @if (canEdit) {
      <button
        class="btn btn-primary"
        (click)="saveClinicProfile()"
        [disabled]="isLoading || isSaving"
      >
        <i class="bi bi-floppy-fill me-2"></i>
        {{ isSaving ? 'Saving...' : 'Save Practice Profile' }}
      </button>
    }
  </div>

  @if (isManager) {
    <div class="card-footer bg-white border-top clinic-deactivate-section">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <div class="fw-semibold text-danger">Deactivate Practice</div>
          <small class="text-muted"
            >Available once no doctors or receptionists are active at this practice. User accounts
            are not deactivated.</small
          >
        </div>
        <button
          class="btn btn-outline-danger"
          (click)="deactivateClinic()"
          [disabled]="!canDeactivateClinic"
        >
          @if (isDeactivating) {
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          } @else {
            <i class="bi bi-slash-circle me-2"></i>
          }
          {{ isDeactivating ? 'Deactivating...' : 'Deactivate Practice' }}
        </button>
      </div>
    </div>
  }
</div>
